import {
  DebugContainer,
  DebugInfo,
  DebugInfoContainer,
  DebugLink,
} from '@/app/components/search/components/Banners/Debug/styled.ts';
import React from 'react';
import { isValidDate } from '@/app/components/search/Utilities.ts';
import { getEndPointPrefix } from '@/app/common/memsearch/getMemsearchEndPoint.ts';
import { SearchModes } from '@/domainLogic/index.ts';
import { APIType, Platform } from '@/app/common/memsearch/memsearchTypes.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { getSearchParamsClient } from '@/app/components/search/common/getSearchParams.ts';
import { DebugAuth } from '@/app/components/common/DebugAuth.tsx';
import { DebugUserIdLine } from '@/app/components/common/DebugUserId.tsx';
import { DebugValuesLines } from '@/app/components/common/DebugValues.tsx';
import { createShowDebugContent } from '@/app/components/search/utils/debugUtils.ts';

const DebugBanner = () => {
  const {
    commonData,
    searchInfoData,
    curiosityParams,
    localization,
    searchParams: searchParamsServer,
  } = usePageWrapperData();
  const { productsData } = useSearchPageData();
  // Self-gating, like the detail-page DebugPanel: the condition lives here rather than being
  // repeated at both mount points and left to drift. Debug has to be switched on for the page —
  // `debug=true` in the URL, or a telesales session. The `debug_values` toggle decides what the
  // banner carries, never whether it opens. (MPH-1086)
  if (!curiosityParams?.debugEnabled) return null;

  const searchParams = getSearchParamsClient(searchParamsServer);
  // Guarded now that the banner mounts on every results render: the products are still undefined
  // while the first search is in flight.
  const { debugData, businessProfileSearchResponse, rankingBusinessProfile } = productsData ?? {};
  const { searchMode } = searchInfoData;
  const apiUrl = debugData?.apiUrl;
  const dateFrom = searchParams?.[URLParameters?.DATE_FROM];
  const vcSearchId = searchParams?.[URLParameters?.VC_SEARCH_ID];
  const isLiveSearch = isValidDate(new Date(dateFrom));
  const hotelUrlToReplace = getEndPointPrefix({
    searchMode: SearchModes.HO,
    apiType: APIType.PRODUCT,
    platform: Platform.OPENX,
  });
  const hotelFlightUrlToReplace = getEndPointPrefix({
    searchMode: SearchModes.DP,
    apiType: APIType.PRODUCT,
    platform: Platform.OPENX,
  });
  const prefixReplaced = apiUrl
    ? apiUrl.replace(`${hotelUrlToReplace}?`, 'debug/vcQuery?').replace(`${hotelFlightUrlToReplace}?`, 'debug/vcQuery?')
    : '';
  const VCAPIQuery = `${prefixReplaced}&search_mode=${searchMode}`;
  const showDebugContent = createShowDebugContent({ commonData, localization, curiosityParams });

  const debugContent = showDebugContent ? (
    <>
      <div>
        <DebugLink href={apiUrl} target="_blank" title="Memsearch API Query" rel="noreferrer">
          Memsearch API Query
        </DebugLink>
        {isLiveSearch && (
          <DebugLink href={VCAPIQuery} target="_blank" title="VC API Query" rel="noreferrer">
            VC API Query
          </DebugLink>
        )}
      </div>
      <DebugInfoContainer>
        {/* Same opt-in for an authorised viewer as for an anonymous one: the id appears only
            when `debug_values` lists `userId`, so there is one rule rather than one per branch. */}
        <DebugUserIdLine />

        {vcSearchId && (
          <DebugInfo>
            VC Search Id: <strong>{vcSearchId}</strong>
          </DebugInfo>
        )}
        {businessProfileSearchResponse && (
          <DebugInfo>
            Business profile: <strong>{businessProfileSearchResponse}</strong>
          </DebugInfo>
        )}
        {rankingBusinessProfile && (
          <DebugInfo>
            Ranking business profile: <strong>{rankingBusinessProfile}</strong>
          </DebugInfo>
        )}
      </DebugInfoContainer>
    </>
  ) : (
    <>
      <DebugUserIdLine />
      <DebugAuth />
    </>
  );

  return (
    <DebugContainer>
      {debugContent}
      <DebugValuesLines />
    </DebugContainer>
  );
};

export default DebugBanner;
